import { supabase } from '../lib/supabase';
import genAI, { handleGeminiError, getSafetySettings } from '../ai/geminiClient';
import { profileService } from './profileService';

/**
 * Fetches comprehensive dashboard analytics for job seekers
 * @param {string} userId - The user's ID
 * @returns {Promise<Object>} Dashboard analytics data
 */
export async function getDashboardAnalytics(userId) {
  try {
    // Fetch application performance metrics - FIXED: Use correct column names and table structure
    const { data: applications, error: appError } = await supabase
      ?.from('job_applications')
      ?.select(`
        id, 
        status, 
        applied_at,
        jobs (
          title,
          companies (
            name
          )
        )
      `)
      ?.eq('applicant_id', userId)
      ?.order('applied_at', { ascending: false });

    if (appError) throw appError;

    // Fetch profile views tracking
    const { data: profileViews, error: viewsError } = await supabase
      ?.from('profile_views')
      ?.select('id, viewed_at, company_id')
      ?.eq('profile_id', userId)
      ?.order('viewed_at', { ascending: false })
      ?.limit(10);

    if (viewsError) throw viewsError;

    // Calculate application performance metrics
    const performanceMetrics = calculatePerformanceMetrics(applications);

    // Get profile view statistics
    const viewStats = calculateViewStats(profileViews);

    return {
      applications: {
        total: applications?.length || 0,
        byStatus: performanceMetrics?.byStatus,
        recentApplications: applications?.slice(0, 5) || [],
        successRate: performanceMetrics?.successRate || 0,
        averageResponseTime: performanceMetrics?.averageResponseTime || 0
      },
      profileViews: {
        total: profileViews?.length || 0,
        recent: profileViews || [],
        topCompanies: viewStats?.topCompanies || [],
        viewTrend: viewStats?.viewTrend || []
      },
      timestamp: new Date()?.toISOString()
    };
  } catch (error) {
    console.error('Error fetching dashboard analytics:', error);
    throw error;
  }
}

/**
 * Calculates application performance metrics
 * @param {Array} applications - Array of job applications
 * @returns {Object} Performance metrics
 */
function calculatePerformanceMetrics(applications) {
  const byStatus = {
    pending: 0,
    reviewing: 0,
    shortlisted: 0,
    interview: 0,
    offered: 0,
    rejected: 0
  };

  applications?.forEach(app => {
    const status = app?.status?.toLowerCase() || 'pending';
    if (byStatus?.[status] !== undefined) {
      byStatus[status]++;
    }
  });

  const total = applications?.length || 0;
  const successfulStates = byStatus?.shortlisted + byStatus?.interview + byStatus?.offered;
  const successRate = total > 0 ? ((successfulStates / total) * 100)?.toFixed(1) : 0;

  // Calculate average response time from actual application data
  let totalResponseTime = 0;
  let applicationsWithResponse = 0;
  
  applications?.forEach(app => {
    if (app?.applied_at && app?.updated_at && app?.status !== 'pending') {
      const appliedDate = new Date(app?.applied_at);
      const updatedDate = new Date(app?.updated_at);
      const daysDiff = Math.floor((updatedDate - appliedDate) / (1000 * 60 * 60 * 24));
      
      if (daysDiff >= 0) {
        totalResponseTime += daysDiff;
        applicationsWithResponse++;
      }
    }
  });
  
  const averageResponseTime = applicationsWithResponse > 0 
    ? Math.round(totalResponseTime / applicationsWithResponse)
    : 0;

  return {
    byStatus,
    successRate,
    averageResponseTime
  };
}

/**
 * Calculates profile view statistics
 * @param {Array} views - Array of profile views
 * @returns {Object} View statistics
 */
function calculateViewStats(views) {
  const companyViewCount = {};
  
  views?.forEach(view => {
    const companyId = view?.company_id || 'Unknown';
    companyViewCount[companyId] = (companyViewCount?.[companyId] || 0) + 1;
  });

  const topCompanies = Object.entries(companyViewCount)
    ?.sort((a, b) => b?.[1] - a?.[1])
    ?.slice(0, 5)
    ?.map(([id, count]) => ({ id, count }));

  // Calculate view trend (last 7 days)
  const viewTrend = calculateViewTrend(views);

  return {
    topCompanies,
    viewTrend
  };
}

/**
 * Calculates view trend for the last 7 days
 * @param {Array} views - Array of profile views
 * @returns {Array} Daily view counts
 */
function calculateViewTrend(views) {
  const today = new Date();
  const trend = [];

  for (let i = 6; i >= 0; i--) {
    const date = new Date(today);
    date?.setDate(date?.getDate() - i);
    const dateStr = date?.toISOString()?.split('T')?.[0];
    
    const count = views?.filter(view => {
      const viewDate = new Date(view?.viewed_at)?.toISOString()?.split('T')?.[0];
      return viewDate === dateStr;
    })?.length || 0;

    trend?.push({ date: dateStr, count });
  }

  return trend;
}

/**
 * Generates AI-powered market insights using Gemini
 * @param {string} role - Job role/title
 * @param {string} location - Job location
 * @param {Array} userSkills - User's skills
 * @returns {Promise<Object>} Market insights
 */
export async function generateMarketInsights(role, location, userSkills = []) {
  try {
    const model = genAI?.getGenerativeModel({ 
      model: 'gemini-2.5-flash',
      generationConfig: {
        temperature: 0.7,
        topP: 0.8,
        topK: 40,
        maxOutputTokens: 1024,
      },
      safetySettings: getSafetySettings()
    });

    const prompt = `As a job market analyst, provide detailed insights for the following job market scenario:

Role: ${role}
Location: ${location}
User Skills: ${userSkills?.join(', ') || 'Not specified'}

Please provide:
1. Salary Range: Median salary range in USD (provide min and max as numbers)
2. Demand Trend: Current demand percentage change (as a positive or negative number)
3. Top 5 In-Demand Skills: Most sought-after skills for this role
4. Market Insights: 3-4 key insights about job opportunities, market trends, and career growth
5. Demand Trend Data: 12 monthly data points showing demand trend (as numbers)

Format your response as JSON with this structure:
{
  "salary": {
    "min": number,
    "max": number,
    "currency": "USD",
    "sampleSize": number
  },
  "demandChange": number,
  "topSkills": ["skill1", "skill2", "skill3", "skill4", "skill5"],
  "insights": ["insight1", "insight2", "insight3"],
  "demandTrend": [number1, number2, ..., number12]
}

Provide realistic, current market data based on 2024-2025 trends.`;

    const result = await model?.generateContent(prompt);
    const response = await result?.response;
    const text = response?.text();

    // Parse JSON response
    const jsonMatch = text?.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      const insights = JSON.parse(jsonMatch?.[0]);
      return {
        ...insights,
        generatedAt: new Date()?.toISOString()
      };
    }

    throw new Error('Failed to parse market insights');
  } catch (error) {
    console.error('Error generating market insights:', error);
    const errorMessage = handleGeminiError(error);
    throw new Error(errorMessage);
  }
}

/**
 * Generates personalized job search tips using Gemini AI
 * @param {Object} userData - User profile and application data
 * @returns {Promise<Array>} Array of personalized tips
 */
export async function generatePersonalizedTips(userData) {
  try {
    const model = genAI?.getGenerativeModel({ 
      model: 'gemini-2.5-flash',
      generationConfig: {
        temperature: 0.8,
        topP: 0.9,
        topK: 40,
        maxOutputTokens: 512,
      },
      safetySettings: getSafetySettings()
    });

    const prompt = `As a career coach, provide 3-4 personalized, actionable job search tips based on this user's profile:

Applications: ${userData?.totalApplications || 0}
Success Rate: ${userData?.successRate || 0}%
Profile Views: ${userData?.profileViews || 0}
Skills: ${userData?.skills?.join(', ') || 'Not specified'}
Target Role: ${userData?.targetRole || 'Not specified'}

Focus on:
- Profile optimization
- Application strategy
- Networking recommendations
- Skill development

Provide specific, actionable tips. Format as a JSON array of strings:
["tip1", "tip2", "tip3", "tip4"]`;

    const result = await model?.generateContent(prompt);
    const response = await result?.response;
    const text = response?.text();

    // Parse JSON response
    const jsonMatch = text?.match(/\[[\s\S]*\]/);
    if (jsonMatch) {
      return JSON.parse(jsonMatch?.[0]);
    }

    // Fallback tips if parsing fails
    return [
      'Optimize your profile with relevant keywords to increase visibility',
      'Follow up on applications after 1 week to show continued interest',
      'Network with professionals in your target industry on LinkedIn',
      'Consider upskilling in emerging technologies relevant to your field'
    ];
  } catch (error) {
    console.error('Error generating personalized tips:', error);
    // Return fallback tips on error
    return [
      'Keep your profile updated with recent achievements',
      'Tailor your applications to each job posting',
      'Expand your professional network regularly',
      'Stay active in industry-related communities'
    ];
  }
}

/**
 * Tracks profile views by companies
 * @param {string} profileId - The user's profile ID
 * @param {string} companyId - The company's ID
 * @returns {Promise<void>}
 */
export async function trackProfileView(profileId, companyId) {
  try {
    const { error } = await supabase?.from('profile_views')?.insert({
        profile_id: profileId,
        company_id: companyId,
        viewed_at: new Date()?.toISOString()
      });

    if (error) throw error;
  } catch (error) {
    console.error('Error tracking profile view:', error);
    throw error;
  }
}

/**
 * Gets job search progress tracking
 * @param {string} userId - The user's ID
 * @param {number} days - Number of days to track (default 30)
 * @returns {Promise<Object>} Progress tracking data
 */
export async function getJobSearchProgress(userId, days = 30) {
  try {
    const startDate = new Date();
    startDate?.setDate(startDate?.getDate() - days);

    // FIXED: Use correct column name 'applicant_id' instead of 'job_seeker_id'
    const { data: applications, error } = await supabase
      ?.from('job_applications')
      ?.select('id, status, applied_at')
      ?.eq('applicant_id', userId)
      ?.gte('applied_at', startDate?.toISOString())
      ?.order('applied_at', { ascending: true });

    if (error) throw error;

    // Group by week
    const weeklyProgress = {};
    applications?.forEach(app => {
      const date = new Date(app?.applied_at);
      const weekNum = Math.floor((date - startDate) / (7 * 24 * 60 * 60 * 1000));
      const weekKey = `Week ${weekNum + 1}`;
      
      if (!weeklyProgress?.[weekKey]) {
        weeklyProgress[weekKey] = { applied: 0, interviewing: 0, offered: 0 };
      }
      
      weeklyProgress[weekKey].applied++;
      if (['interview', 'shortlisted']?.includes(app?.status?.toLowerCase())) {
        weeklyProgress[weekKey].interviewing++;
      }
      if (app?.status?.toLowerCase() === 'offered') {
        weeklyProgress[weekKey].offered++;
      }
    });

    return {
      weeklyProgress,
      totalApplications: applications?.length || 0,
      daysTracked: days
    };
  } catch (error) {
    console.error('Error fetching job search progress:', error);
    throw error;
  }
}

/**
 * Get dashboard analytics with profile context
 */
export async function getDashboardAnalyticsWithProfile(userId) {
  try {
    // Get basic analytics
    const analytics = await getDashboardAnalytics(userId);

    // Get profile completeness
    const profileCompleteness = await profileService?.getProfileCompleteness(userId);

    // Get profile views analytics
    const profileViews = await profileService?.getProfileViewsAnalytics(userId, 30);

    // Get profile summary
    const profile = await profileService?.getProfileSummary(userId);

    return {
      ...analytics,
      profile: {
        completeness: profileCompleteness,
        views: profileViews,
        summary: profile
      },
      recommendations: generateProfileBasedRecommendations(
        analytics,
        profileCompleteness,
        profileViews
      )
    };
  } catch (error) {
    console.error('Error getting dashboard analytics with profile:', error);
    throw error;
  }
}

/**
 * Generate profile-based recommendations
 */
export function generateProfileBasedRecommendations(analytics, completeness, views) {
  const recommendations = [];

  // Profile completeness recommendations
  if (completeness?.totalScore < 70) {
    recommendations?.push({
      type: 'profile_completion',
      priority: 'high',
      message: 'Complete your profile to increase visibility',
      action: 'Complete profile',
      suggestions: completeness?.suggestions
    });
  }

  // Profile views recommendations
  if (views?.total < 10) {
    recommendations?.push({
      type: 'profile_visibility',
      priority: 'medium',
      message: 'Increase your profile visibility to attract more opportunities',
      action: 'Optimize profile',
      suggestions: [
        'Add a professional photo',
        'Update your headline',
        'Add more skills',
        'Share your profile on LinkedIn'
      ]
    });
  }

  // Application recommendations based on profile
  if (analytics?.applicationStats?.total < 5) {
    recommendations?.push({
      type: 'applications',
      priority: 'high',
      message: 'Start applying to jobs that match your profile',
      action: 'Browse jobs',
      suggestions: [
        'Review recommended jobs',
        'Set up job alerts',
        'Update job preferences'
      ]
    });
  }

  // Response rate recommendations
  if (analytics?.applicationStats?.total > 10) {
    const responseRate = (analytics?.applicationStats?.interviewing + 
                         analytics?.applicationStats?.offered) / 
                         analytics?.applicationStats?.total;
    
    if (responseRate < 0.1) {
      recommendations?.push({
        type: 'application_quality',
        priority: 'high',
        message: 'Improve your application success rate',
        action: 'Optimize applications',
        suggestions: [
          'Tailor your resume to each job',
          'Write personalized cover letters',
          'Highlight relevant skills',
          'Follow up on applications'
        ]
      });
    }
  }

  return recommendations;
}

/**
 * Get profile-based career insights
 */
export async function getProfileBasedCareerInsights(userId) {
  try {
    const [profile, analytics, completeness] = await Promise.all([
      profileService?.getCompleteProfile(userId),
      getDashboardAnalytics(userId),
      profileService?.getProfileCompleteness(userId)
    ]);

    return {
      profileStrengths: analyzeProfileStrengths(profile),
      careerProgression: analyzeCareerProgression(profile?.workExperience),
      skillsGapAnalysis: analyzeSkillsGaps(profile, analytics),
      marketPosition: analyzeMarketPosition(profile, analytics),
      improvementAreas: completeness?.suggestions
    };
  } catch (error) {
    console.error('Error getting profile-based career insights:', error);
    throw error;
  }
}

/**
 * Analyze profile strengths
 */
export function analyzeProfileStrengths(profile) {
  const strengths = [];

  if (profile?.yearsOfExperience > 5) {
    strengths?.push({
      area: 'Experience',
      message: `${profile?.yearsOfExperience} years of professional experience`,
      impact: 'high'
    });
  }

  if (profile?.skills?.length >= 10) {
    strengths?.push({
      area: 'Skills',
      message: `Strong skill set with ${profile?.skills?.length} skills`,
      impact: 'high'
    });
  }

  if (profile?.education?.length > 0) {
    const degrees = profile?.education?.map(e => e?.degree)?.join(', ');
    strengths?.push({
      area: 'Education',
      message: `Academic background: ${degrees}`,
      impact: 'medium'
    });
  }

  if (profile?.certifications?.length > 0) {
    strengths?.push({
      area: 'Certifications',
      message: `${profile?.certifications?.length} professional certifications`,
      impact: 'medium'
    });
  }

  return strengths;
}

/**
 * Analyze career progression
 */
export function analyzeCareerProgression(workExperience) {
  if (!workExperience || workExperience?.length === 0) {
    return {
      trend: 'insufficient_data',
      message: 'Add work experience to see career progression'
    };
  }

  const sortedExperience = [...workExperience]?.sort((a, b) => 
    new Date(b.startDate) - new Date(a.startDate)
  );

  // Analyze job titles for progression
  const titleProgression = sortedExperience?.map(exp => exp?.jobTitle);
  
  return {
    trend: 'stable',
    currentRole: sortedExperience?.[0]?.jobTitle,
    yearsInCurrentRole: calculateYearsInRole(sortedExperience?.[0]),
    totalPositions: sortedExperience?.length,
    companies: [...new Set(sortedExperience.map(e => e.company))]?.length
  };
}

/**
 * Calculate years in role
 */
export function calculateYearsInRole(experience) {
  if (!experience) return 0;
  
  const startDate = new Date(experience.startDate);
  const endDate = experience?.endDate ? new Date(experience.endDate) : new Date();
  const years = (endDate - startDate) / (1000 * 60 * 60 * 24 * 365);
  
  return Math.round(years * 10) / 10;
}

/**
 * Analyze skills gaps
 */
export function analyzeSkillsGaps(profile, analytics) {
  const gaps = [];

  // Analyze based on application success rate
  if (analytics?.applicationStats?.total > 5) {
    const successRate = (analytics?.applicationStats?.interviewing + 
                         analytics?.applicationStats?.offered) / 
                         analytics?.applicationStats?.total;
    
    if (successRate < 0.15) {
      gaps?.push({
        area: 'Application Success',
        message: 'Low application response rate - consider adding in-demand skills',
        priority: 'high'
      });
    }
  }

  // Check for technical skills
  const hasTechnicalSkills = profile?.skills?.some(skill => 
    ['javascript', 'python', 'java', 'react', 'node']?.some(tech =>
      skill?.skillName?.toLowerCase()?.includes(tech)
    )
  );

  if (!hasTechnicalSkills && profile?.yearsOfExperience < 3) {
    gaps?.push({
      area: 'Technical Skills',
      message: 'Consider adding technical skills to expand opportunities',
      priority: 'medium'
    });
  }

  return gaps;
}

/**
 * Analyze market position
 */
export function analyzeMarketPosition(profile, analytics) {
  return {
    profileViews: analytics?.profile?.views?.total || 0,
    applicationRate: analytics?.applicationStats?.total || 0,
    competitiveness: calculateCompetitiveness(profile, analytics),
    recommendations: [
      'Keep profile updated',
      'Apply to matching positions',
      'Network with recruiters'
    ]
  };
}

/**
 * Calculate competitiveness score
 */
export function calculateCompetitiveness(profile, analytics) {
  let score = 0;

  // Profile completeness
  if (profile?.resumeUrl) score += 20;
  if (profile?.skills?.length >= 5) score += 20;
  if (profile?.workExperience?.length > 0) score += 20;
  if (profile?.education?.length > 0) score += 15;
  if (profile?.certifications?.length > 0) score += 10;

  // Activity level
  if (analytics?.applicationStats?.total > 10) score += 15;

  return Math.min(score, 100);
}

/**
 * Subscribe to real-time profile views updates
 * @param {string} userId - User ID to subscribe to
 * @param {function} callback - Callback function to handle updates
 * @returns {function} Unsubscribe function
 */
export const subscribeToProfileViews = (userId, callback) => {
  const channel = supabase
    ?.channel('profile-views-changes')
    ?.on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'profile_views',
        filter: `profile_id=eq.${userId}`
      },
      async (payload) => {
        const profileView = payload?.new;

        if (profileView) {
          // Fetch company details for the view
          let companyDetails = null;
          if (profileView?.company_id) {
            const { data: company } = await supabase
              ?.from('companies')
              ?.select('id, name, logo_url, industry')
              ?.eq('id', profileView?.company_id)
              ?.single();

            if (company) {
              companyDetails = {
                id: company?.id,
                name: company?.name,
                logoUrl: company?.logo_url,
                industry: company?.industry
              };
            }
          }

          callback({
            id: profileView?.id,
            profileId: profileView?.profile_id,
            companyId: profileView?.company_id,
            viewedAt: profileView?.viewed_at,
            company: companyDetails
          });
        }
      }
    )
    ?.subscribe();

  // Return unsubscribe function
  return () => {
    supabase?.removeChannel(channel);
  };
};

/**
 * Subscribe to real-time dashboard data updates
 * Monitors job applications, profile views, and other dashboard metrics
 * @param {string} userId - User ID to subscribe to
 * @param {function} callback - Callback function to handle updates
 * @returns {function} Unsubscribe function
 */
export const subscribeToDashboardUpdates = (userId, callback) => {
  const channels = [];

  // Subscribe to job applications
  const applicationsChannel = supabase
    ?.channel('dashboard-applications')
    ?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'job_applications',
        filter: `applicant_id=eq.${userId}`
      },
      () => {
        callback({ type: 'applications', action: 'refresh' });
      }
    )
    ?.subscribe();

  channels?.push(applicationsChannel);

  // Subscribe to profile views
  const profileViewsChannel = supabase
    ?.channel('dashboard-profile-views')
    ?.on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'profile_views',
        filter: `profile_id=eq.${userId}`
      },
      () => {
        callback({ type: 'profile_views', action: 'refresh' });
      }
    )
    ?.subscribe();

  channels?.push(profileViewsChannel);

  // Subscribe to job recommendations
  const recommendationsChannel = supabase
    ?.channel('dashboard-recommendations')
    ?.on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'job_recommendations',
        filter: `user_id=eq.${userId}`
      },
      () => {
        callback({ type: 'recommendations', action: 'refresh' });
      }
    )
    ?.subscribe();

  channels?.push(recommendationsChannel);

  // Return unsubscribe function
  return () => {
    channels?.forEach(channel => supabase?.removeChannel(channel));
  };
};

/**
 * Subscribe to job search progress updates
 * @param {string} userId - User ID to subscribe to
 * @param {function} callback - Callback function to handle updates
 * @returns {function} Unsubscribe function
 */
export const subscribeToJobSearchProgress = (userId, callback) => {
  const channel = supabase
    ?.channel('job-search-progress')
    ?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'job_applications',
        filter: `applicant_id=eq.${userId}`
      },
      async (payload) => {
        const application = payload?.new || payload?.old;
        
        if (application) {
          // Fetch updated progress data
          const progressData = await getJobSearchProgress(userId, 30);
          
          callback({
            type: 'progress_update',
            application: {
              id: application?.id,
              status: application?.status,
              appliedAt: application?.applied_at
            },
            progressData
          });
        }
      }
    )
    ?.subscribe();

  // Return unsubscribe function
  return () => {
    supabase?.removeChannel(channel);
  };
};